import React from "react";
import { ShoppingCart, PackageOpen, ArrowRight } from "lucide-react";
import CartItemCard from "./CartItemCard";
import type { CartItem } from "./types";

interface CartListProps {
  cartItems: CartItem[];
  cartLoading: boolean;
  enhancedLoading: boolean;
  onQuantityChange: (id: number, quantity: number) => void;
  onRemoveItem: (id: number) => void;
  isLoading: boolean;
}

const CartList: React.FC<CartListProps> = ({
  cartItems,
  cartLoading,
  enhancedLoading,
  onQuantityChange,
  onRemoveItem,
  isLoading,
}) => {
  if (cartLoading) {
    return (
      <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden">
        <div className="p-6 border-b border-neutral-800">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-white">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Your Cart
          </h2>
        </div>
        <div className="p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            <span className="text-neutral-400 font-medium">
              Loading your cart...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden">
        <div className="p-6 border-b border-neutral-800">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-white">
            <ShoppingCart className="w-6 h-6 text-primary" />
            Your Cart
          </h2>
        </div>
        <div className="p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl opacity-50"></div>
              <PackageOpen
                className="relative w-24 h-24 text-neutral-600 mx-auto"
                strokeWidth={1.5}
              />
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">
              Your cart is empty
            </h3>
            <p className="text-neutral-400 mb-8 leading-relaxed">
              Looks like you have not added anything to your cart yet. Start
              shopping to fill it up!
            </p>
            <button
              onClick={() => (window.location.href = "/products")}
              className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-black px-8 py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              <ShoppingCart className="w-5 h-5" />
              Start Shopping
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 rounded-2xl shadow-xl border border-neutral-800 overflow-hidden">
      <div className="p-6 border-b border-neutral-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-3 text-white">
              <ShoppingCart className="w-6 h-6 text-primary" />
              Your Cart
            </h2>
          </div>
          {enhancedLoading && (
            <div className="flex items-center gap-2 text-sm text-primary bg-primary/10 px-4 py-2 rounded-full">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
              <span className="font-medium">Updating...</span>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-neutral-900/50 border-b border-neutral-800">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-primary text-black flex items-center justify-center font-bold shadow-lg mb-2 ring-4 ring-primary/20">
              1
            </div>
            <span className="text-xs font-semibold text-primary">Cart</span>
          </div>
          <div className="flex-1 h-1 bg-neutral-800 mx-2 rounded-full"></div>
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-neutral-800 text-neutral-500 flex items-center justify-center font-bold mb-2">
              2
            </div>
            <span className="text-xs font-semibold text-neutral-500">
              Address
            </span>
          </div>
          <div className="flex-1 h-1 bg-neutral-800 mx-2 rounded-full"></div>
          <div className="flex flex-col items-center flex-1">
            <div className="w-10 h-10 rounded-full bg-neutral-800 text-neutral-500 flex items-center justify-center font-bold mb-2">
              3
            </div>
            <span className="text-xs font-semibold text-neutral-500">
              Payment
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="space-y-5">
          {cartItems
            .slice()
            .reverse()
            .map((item) => (
              <CartItemCard
                key={item.id}
                item={item}
                onQuantityChange={onQuantityChange}
                onRemove={onRemoveItem}
                isLoading={isLoading}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default CartList;
